// src/components/Layout.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { MegaMenu } from 'primereact/megamenu';
import './Layout.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import axiosInstance from '../services/axiosInstance';
import { toYmd } from '../utils/dateOnly';
import { handleApiError } from "../components/apiErrorHandler";
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Sidebar } from 'primereact/sidebar';
import { Badge } from 'primereact/badge';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from "primereact/toast";
import { UserContext } from '../contexts/UserContext';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import OperatorSecurityDialog from './OperatorSecurityDialog';
import { PageLoadingOverlay } from './GoldfishLoader';

const hasPerm = (user, codename) => {
    if (!user?.permissions) return false;
    if (user.permissions.includes('*')) return true;    // is_admin wildcard
    return user.permissions.includes(codename);
};

const hasAnyPerm = (user, ...codenames) => {
    if (!user?.permissions) return false;
    if (user.permissions.includes('*')) return true;
    return codenames.some(c => user.permissions.includes(c));
};

const Layout = () => {
    const navigate = useNavigate();
    const [Name, setName] = useState(null);
    const [orgData, setOrgData] = useState([]);
    const [notifications, setNotifications] = useState(0);
    const [notificationsData, setNotificationsData] = useState([]);
    const [sideBarVisible, setSideBarVisible] = useState(false);
    const [showOperatorSecurity, setShowOperatorSecurity] = useState(false);
    const op = useRef(null);
    const location = useLocation();
    const { breadcrumbs, goTo, reset, isDrilldown, endDrilldown, isBreadcrumbNav, endBreadcrumbNav } = useBreadcrumbs();
    const [showLedgerDateDialog, setShowLedgerDateDialog] = useState(false);
    const [ledgerRange, setLedgerRange] = useState([new Date(), new Date()]);
    const [showTrialBalanceDialog, setShowTrialBalanceDialog] = useState(false);
    const [trialBalanceDate, setTrialBalanceDate] = useState(new Date());
    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState(null);
    const toast = useRef(null);
    const refillSound = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const orgResult = await axiosInstance.get('/api/orgsettings/');
                setOrgData(orgResult.data);
                if (orgResult.data.length > 0) {
                    localStorage.setItem('organizationSettings', JSON.stringify(orgResult.data[0]));
                    const theme = orgResult.data[0]?.custom_json_settings?.branding?.theme || "fluent-light";
                    applyTheme(theme);
                }
                const userResult = await axiosInstance.get('/api/user/me/');
                setName(userResult.data);
                localStorage.setItem('userSettings', JSON.stringify(userResult.data));
            } catch (error) {
                handleApiError(error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const audio = new Audio("/sounds/alert.mp3");
        audio.addEventListener("error", () => {});
        refillSound.current = audio;
    }, []);

    useEffect(() => {
        const theme = orgData?.[0]?.custom_json_settings?.branding?.theme;
        if (theme) applyTheme(theme);
    }, [orgData]);

    useEffect(() => {
        axiosInstance.get("/api/departments")
            .then(res => setDepartments(res.data || []))
            .catch(err => console.error("Dept load failed", err));
    }, []);

    useEffect(() => {
        if (isBreadcrumbNav) { endBreadcrumbNav(); return; }
        if (isDrilldown)     { endDrilldown();     return; }
        reset([]);
    }, [location.pathname]);

    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const response = await axiosInstance.get("/api/notifications/");
                setNotifications(response.data.filter(n => !n.is_read).length);
                setNotificationsData(response.data);
            } catch (error) {
                handleApiError(error);
            }
        };
        fetchNotifications();
    }, [navigate]);

    useEffect(() => {
        // Read orgId from localStorage — only attempt after orgData has been fetched and stored
        const orgId = orgData?.[0]?.organization_id;
        if (!orgId) return;

        const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';
        const wsUrl = baseUrl
            .replace('https://', 'wss://')
            .replace('http://', 'ws://');

        let socket;
        let reconnectTimer;
        let destroyed = false;

        const connect = () => {
            if (destroyed) return;
            socket = new WebSocket(`${wsUrl}/ws/org/${orgId}/`);

            socket.onopen = () => { console.log("WebSocket connected"); };

            socket.onmessage = (event) => {
                if (destroyed) return;
                const data = JSON.parse(event.data);

                // Ignore portal-targeted notifications
                if (data.target === 'portal') return;

                if (data.type === "notification" && data.target === 'pharmacy') {
                    if (refillSound.current) {
                        refillSound.current.currentTime = 0;
                        refillSound.current.play().catch(() => {});
                    }
                    toast.current.show({
                        severity: "warn", summary: data.title || "New Notification",
                        detail: (
                            <div>
                                <div>{data.message}</div>
                                {data.entity_route && (
                                    <Button label="View" className="p-button-sm p-button-text" onClick={() => navigate(data.entity_route)} />
                                )}
                            </div>
                        ),
                        life: 7000
                    });
                    setNotificationsData(prev => [{ id: data.notification_id, title: data.title, message: data.message, entity_type: data.entity_type, entity_id: data.entity_id, entity_route: data.entity_route, created_at: data.created_at, is_read: false }, ...prev]);
                    setNotifications(prev => prev + 1);
                }
            };

            socket.onclose = () => {
                console.log("WebSocket disconnected");
                if (!destroyed) reconnectTimer = setTimeout(connect, 3000);
            };
        };

        connect();
        return () => {
            destroyed = true;
            clearTimeout(reconnectTimer);
            if (socket && socket.readyState !== WebSocket.CLOSED) socket.close();
        };
    }, [orgData]);

    const getBranding = () => orgData?.[0]?.custom_json_settings?.branding || {};

    // Set manually via Django admin per-customer (see gf/models.py::OrganizationSettings.crm_only).
    // Hides invoice-dependent CRM UI (manual refill reminders, manual order-ready button)
    // for customers who invoice through an external/offline POS instead of Goldfish.
    const crmOnly = orgData?.[0]?.crm_only === true;

    const applyTheme = (themeName) => {
        const id = "app-theme";
        let link = document.getElementById(id);
        if (!link) {
            link = document.createElement("link");
            link.id   = id;
            link.rel  = "stylesheet";
            document.head.appendChild(link);
        }
        link.href = `/themes/${themeName}/theme.css`;
    };

    const markAllNotificationsRead = async () => {
        try {
            await axiosInstance.post('/api/notifications/mark-all-read/');
            setNotificationsData(prev => prev.map(n => ({ ...n, is_read: true })));
            setNotifications(0);
        } catch (error) {
            console.error("Error marking all notifications as read:", error);
        }
    };

    const handleNotificationClick = async (notification) => {
        const notificationId = notification.notification_id || notification.id;
        try {
            if (!notification.is_read && notificationId) {
                await axiosInstance.post(`/api/notifications/${notificationId}/mark-as-read/`);
                setNotificationsData(prev => prev.map(n => (n.notification_id || n.id) === notificationId ? { ...n, is_read: true } : n));
                setNotifications(prev => (prev > 0 ? prev - 1 : 0));
            }
            if (notification.move_order_id)             { navigate(`/moveorders?move_order=${notification.move_order_id}`); setSideBarVisible(false); }
            if (notification.entity_type === "refill_req") { navigate(notification.entity_route); setSideBarVisible(false); }
            if (notification.entity_type === "rx_upload")  { navigate(notification.entity_route); setSideBarVisible(false); }
            if (notification.entity_type === "clinic_rx")  { navigate(notification.entity_route); setSideBarVisible(false); }
        } catch (error) {
            console.error("Error handling notification click:", error);
        }
    };

    const downloadFile = async (url, filename) => {
        try {
            const res  = await axiosInstance.get(url, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'text/csv' });
            const link = document.createElement('a');
            const blobUrl = window.URL.createObjectURL(blob);
            link.href  = blobUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('Download failed:', err);
        }
    };

    // Routes through /logout (see pages/Logout.js) so tokens and the cached
    // product catalog (see services/queryClient.js) actually get cleared —
    // navigating straight to /login here previously skipped that entirely.
    const handleLogout = () => navigate('/logout');

    // ── Permission flags — derived from Name state, not from context hooks ───
    // Recomputes only when Name changes (after API response arrives).
    const perms = useMemo(() => ({
        // Existing flag-based gates (unchanged)
        isAdmin:    Name?.isAdmin || Name?.username === 'gfadmin',
        isApprover: Name?.isApprover,

        // Products
        canViewProducts:   hasPerm(Name, 'view_products'),
        canManageProducts: hasPerm(Name, 'manage_products'),
        canViewPriceLists: hasPerm(Name, 'view_price_lists'),
        showProductsMenu:  hasAnyPerm(Name, 'view_products', 'manage_products', 'view_price_lists'),

        // Inventory
        canViewInventory:      hasPerm(Name, 'view_inventory'),
        canViewInventoryAudit: hasPerm(Name, 'view_inventory_audit'),
        canBulkInventory:      hasPerm(Name, 'inventory_bulk_upload'),
        canViewMoveOrders:     hasPerm(Name, 'view_move_orders'),
        canViewSuppliers:      hasPerm(Name, 'view_suppliers'),
        canViewSupplierInv:    hasPerm(Name, 'view_supplier_invoices'),
        canViewSupplierPay:    hasPerm(Name, 'view_supplier_payments'),
        showInventoryMenu:     hasAnyPerm(Name,
            'view_inventory', 'view_inventory_audit', 'inventory_bulk_upload',
            'view_move_orders', 'view_suppliers', 'view_supplier_invoices', 'view_supplier_payments'
        ),

        // Customers
        canViewCustomers:     hasPerm(Name, 'view_customers'),
        canManageCustomers:   hasPerm(Name, 'manage_customers'),
        canViewPrescriptions: hasPerm(Name, 'view_prescriptions'),
        canViewInvoices:      hasPerm(Name, 'view_invoices'),
        canViewPayments:      hasPerm(Name, 'view_payments'),
        canViewCreditNotes:   hasPerm(Name, 'view_credit_notes'),
        canViewRefillReq:     hasPerm(Name, 'view_refill_requests'),
        canViewRefillRem:     hasPerm(Name, 'view_refill_reminders'),
        showCustomersMenu:    hasAnyPerm(Name,
            'view_customers', 'manage_customers', 'view_prescriptions', 'view_invoices', 'view_payments',
            'view_credit_notes', 'view_refill_requests', 'view_refill_reminders'
        ),

        // Finance
        canViewFinance:      hasPerm(Name, 'view_finance_dashboard'),
        canDownloadLedger:   hasPerm(Name, 'download_ledger'),
        canDownloadTrialBal: hasPerm(Name, 'download_trial_balance'),
        showFinanceMenu: hasAnyPerm(Name, 
            'view_finance_dashboard', 'download_ledger', 'download_trial_balance', 'view_reports'
        ),

        //Advanced Reports
        canViewReports:   hasPerm(Name, 'view_reports'),
    }), [Name]);

    // ── Nav items ─────────────────────────────────────────────────────────────
    const items = [
        {
            label: 'Home',
            icon: 'pi pi-home',
            command: () => navigate('/home'),
        },

        // Existing is_admin gate — unchanged
        perms.isAdmin && {
            label: 'Admin Organizations',
            icon: 'pi pi-users',
            items: [[
                { label: 'Departments',   items: [{ label: 'Manage Departments', command: () => navigate('/departments') }] },
                { label: 'Users',         
                    items: [
                        { label: 'Manage Users', command: () => navigate('/register') },
                        { label: 'Manage Permissions', command: () => navigate('/roles') },
                    ] },
                {
                    label: 'Organizations',
                    items: [
                        { label: 'Manage Organization', command: () => navigate('/orgsettings') },
                        ...(Name?.username === 'gfadmin'
                            ? [
                                { label: 'Create Organizations', command: () => navigate('/create-organization') },
                                { label: 'Onboarding - Upload Items',     command: () => navigate('/onboarding-upload') },
                                { label: 'Onboarding - Upload Suppliers', command: () => navigate('/onboarding-upload-sup') },
                              ]
                            : []
                        ),
                    ],
                },
                {
                    label: 'Printers',
                    items: [
                        { label: 'Manage Printers',     command: () => navigate('/printers') },
                        { label: 'Printer Assignments', command: () => navigate('/printer-assignments') },
                    ],
                },
            ]],
        },

        perms.showProductsMenu && {
            label: 'Admin Products',
            icon: 'pi pi-box',
            items: [[{
                label: 'Products',
                items: [
                    perms.canViewProducts   && { label: 'Manage Products',           command: () => navigate('/products') },
                    perms.canManageProducts && { label: 'Manage Product Attributes', command: () => navigate('/product-attributes') },
                    perms.canViewPriceLists && { label: 'Product Price List',        command: () => navigate('/product-pricelists') },
                ].filter(Boolean),
            }]],
        },

        perms.showInventoryMenu && {
            label: 'Inventory',
            icon: 'pi pi-building',
            items: [[
                (perms.canViewInventory || perms.canViewInventoryAudit || perms.canBulkInventory) && {
                    label: 'Manage Inventory',
                    items: [
                        perms.canViewInventory      && { label: 'Inventory Items', command: () => navigate('/inventory') },
                        perms.canViewInventoryAudit && { label: 'Inventory Audit', command: () => navigate('/inventory-audit') },
                        perms.canBulkInventory      && { label: 'Inventory Bulk',  command: () => navigate('/inventory-bulk') },
                    ].filter(Boolean),
                },
                perms.canViewMoveOrders && {
                    label: 'Manage Orders',
                    items: [
                        { label: 'Move Orders', command: () => navigate('/moveorders') },
                        ...(perms.isApprover ? [{ label: 'Move Order Approvals', command: () => navigate('/moveorders?approvals=1') }] : []),
                    ],
                },
                (perms.canViewSuppliers || perms.canViewSupplierInv || perms.canViewSupplierPay) && {
                    label: 'Manage Suppliers',
                    items: [
                        perms.canViewSuppliers   && { label: 'Suppliers',         command: () => navigate('/suppliers') },
                        perms.canViewSupplierInv && { label: 'Supplier Invoices', command: () => navigate('/supplier-invoices') },
                        perms.canViewSupplierPay && { label: 'Supplier Payments', command: () => navigate('/supplier-payments') },
                    ].filter(Boolean),
                },
            ].filter(Boolean)],
        },

        perms.showCustomersMenu && {
            label: 'Customers',
            icon: 'pi pi-user',
            items: [[{
                label: 'Manage Customers',
                items: [
                    perms.canViewCustomers     && { label: 'Customer List',                 command: () => navigate('/customers') },
                    perms.canViewPrescriptions && { label: 'Prescriptions',                command: () => navigate('/prescriptions') },
                    perms.canViewInvoices      && { label: 'Invoices',                     command: () => navigate('/invoices') },
                    perms.canViewPayments      && { label: 'Payments',                     command: () => navigate('/customer-payments') },
                    perms.canViewRefillReq     && { label: 'Portal Refill Requests',       command: () => navigate('/refill-requests') },
                    perms.canViewPrescriptions && { label: 'Portal Prescription Requests', command: () => navigate('/prescription-uploads') },
                    perms.canViewPrescriptions && { label: 'Incoming from Clinics',        command: () => navigate('/clinic-prescriptions') },
                    perms.canViewRefillRem     && { label: 'Refill Reminders',             command: () => navigate('/refill-reminders') },
                    perms.canViewRefillRem && crmOnly && { label: 'Manual Refill Reminders', command: () => navigate('/manual-refill-reminders') },
                    perms.canViewCreditNotes   && { label: 'Credit Notes',                 command: () => navigate('/credit-notes') },
                    perms.canManageCustomers   && { label: 'Broadcast SMS',                command: () => navigate('/broadcast-sms') },
                ].filter(Boolean),
            }]],
        },

        perms.showFinanceMenu && {
            label: 'Finance',
            icon: 'pi pi-chart-line',
            items: [[{
                label: 'Reports',
                items: [
                    perms.canViewFinance      && { label: 'Accounting Dashboard',         command: () => navigate('/accounting-dashboard') },
                    perms.canDownloadLedger   && { label: 'Download Ledger (CSV)',        command: () => setShowLedgerDateDialog(true) },
                    perms.canDownloadTrialBal && { label: 'Download Trial Balance (CSV)', command: () => setShowTrialBalanceDialog(true) },
                    perms.canViewReports      && { label: 'Advanced Reports',             command: () => navigate('/reports') },
                ].filter(Boolean),
            }]],
        },

        {
            label: 'Account',
            icon: 'pi pi-key',
            items: [[{
                label: 'Profile',
                items: [
                    { label: 'Change PIN / QR', command: () => setShowOperatorSecurity(true) },
                    { label: 'Logout',          command: () => navigate('/logout') },
                ],
            }]],
        },
    ].filter(Boolean);

    const end = (
        <div className="app-header-end">
            {/* Organization logo */}
            {getBranding().logo_url && getBranding().show_in_app_header && (
                <img
                    src={getBranding().logo_url}
                    alt="Organization Logo"
                    className="app-header-logo"
                />
            )}

            {/* Pharmacy / Organization name */}
            <span className="app-header-org" title={Name?.organization_name}>
                {Name?.organization_name}
            </span>

            {/* New invoice — available on every page */}
            {hasPerm(Name, 'create_invoices') && (
                <Button
                    icon="pi pi-plus"
                    aria-label="New invoice"
                    tooltip="New invoice"
                    tooltipOptions={{ position: 'bottom' }}
                    className="p-button-rounded p-button-lg app-header-plus"
                    onClick={() => navigate('/invoices', { state: { openCreate: true } })}
                />
            )}

            {/* Notifications bell */}
            <Button
                icon="pi pi-bell"
                className="p-button-rounded p-button-text p-button-plain p-button-lg"
                onClick={() => setSideBarVisible(true)}
            >
                {notifications > 0 && <Badge value={notifications} severity="danger" />}
            </Button>

            {/* Notifications sidebar */}
            <Sidebar
                visible={sideBarVisible}
                position="right"
                onHide={() => setSideBarVisible(false)}
                style={{ width: '400px', padding: '10px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h5 style={{ margin: 0 }}>Notifications</h5>
                    {notifications > 0 && (
                        <Button
                            label="Mark all read"
                            className="p-button-link"
                            style={{ padding: 0, fontSize: '0.85rem' }}
                            onClick={markAllNotificationsRead}
                        />
                    )}
                </div>
                {notificationsData.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <img
                            src="/goldfish-notifications.jpg"
                            alt="No Notifications"
                            style={{ width: '220px', opacity: 0.5, borderRadius: '100px' }}
                        />
                        <p style={{ marginTop: '10px', color: '#666' }}>No unread notifications</p>
                    </div>
                ) : (
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        {notificationsData.map((notification) => (
                            <li
                                key={notification.id}
                                style={{
                                    padding: '10px',
                                    borderBottom: '1px solid #ddd',
                                    backgroundColor: notification.is_read ? '#f0f0f0' : '#fff',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                    {notification.title}
                                </div>
                                <p style={{ margin: 0, marginBottom: 4 }}>
                                    {notification.message}
                                </p>
                                {/* Deep-link to move order if present */}
                                {notification.mo_number && (
                                    <button
                                        type="button"
                                        className="p-button-link"
                                        style={{ padding: 0, fontSize: '0.85rem' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleNotificationClick(notification);
                                        }}
                                    >
                                        View Move Order&nbsp;
                                        <strong>{notification.mo_number}</strong>
                                    </button>
                                )}
                                <div>
                                    <small style={{ color: '#777' }}>
                                        {new Date(notification.created_at).toLocaleString()}
                                    </small>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Sidebar>

            {/* User Avatar (Clickable) */}
            <Avatar icon="pi pi-user" shape="circle" style={{ cursor: 'pointer' }} onClick={(e) => op.current.toggle(e)} />

            {/* Overlay Panel (User Menu) */}
            <OverlayPanel ref={op} style={{ width: '200px' }}>
                <div className="p-text-center">
                    <p style={{ padding: '10px', textAlign: 'center' }}>
                        <strong>{Name?.first_name} {Name?.last_name}</strong>
                    </p>
                </div>
                <div className='flex flex-column gap-2' style={{ margin: '15px' }}>
                    {orgData?.[0]?.custom_json_settings?.operator_authentication?.enabled &&
                        orgData?.[0]?.custom_json_settings?.operator_authentication?.allow_user_pin_change !== false && (
                        <Button
                            label="Change PIN / QR"
                            icon="pi pi-key"
                            className="p-button-text p-button-sm"
                            onClick={() => {
                                op.current.hide();
                                setShowOperatorSecurity(true);
                            }}
                        />
                    )}
                    <Button
                        icon="pi pi-cog"
                        label="My Account"
                        className="p-button-text p-button-sm"
                        onClick={() => { op.current.hide(); navigate('/my-account'); }}
                    />
                    <Button
                        icon="pi pi-sign-out"
                        label="Logout"
                        className="p-button-text p-button-sm"
                        onClick={handleLogout}
                    />
                </div>
            </OverlayPanel>
        </div>
    );

    return (
        <UserContext.Provider value={Name}>
            {!Name && <PageLoadingOverlay />}

            <Toast ref={toast} position="top-center" />
            <div>
                <div>
                    <MegaMenu
                        model={items}
                        orientation="horizontal"
                        breakpoint="768px"
                        className="p-3 surface-0 shadow-2"
                        end={end}
                    />
                    {breadcrumbs.length > 0 && (
                        <div style={{ padding: '6px 20px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem' }}>
                            {breadcrumbs.map((bc, idx) => (
                                <span key={idx}>
                                    <span style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 500 }} onClick={() => { navigate(bc.path, { state: bc.state }); goTo(idx); }}>
                                        {bc.label}
                                    </span>
                                    {idx < breadcrumbs.length - 1 && ' / '}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <Dialog header="Select Ledger Date Range" visible={showLedgerDateDialog} style={{ width: '30vw' }} onHide={() => setShowLedgerDateDialog(false)}>
                    <div className='flex justify-content-end gap-2 mt-3'>
                        <Calendar value={ledgerRange} onChange={(e) => setLedgerRange(e.value)} selectionMode="range" showIcon />
                        <Dropdown value={selectedDept} options={[{ id: null, department: "All Departments" }, ...departments]} optionLabel="department" optionValue="id" onChange={(e) => setSelectedDept(e.value)} placeholder="Filter by Department" className="w-20rem" />
                    </div>
                    <div className="flex justify-content-end gap-2 mt-3">
                        <Button label="Cancel" className="p-button-text" onClick={() => setShowLedgerDateDialog(false)} />
                        <Button label="Download" onClick={async () => {
                            const from = toYmd(ledgerRange[0]);
                            const to   = toYmd(ledgerRange[1]);
                            const deptParam = selectedDept ? `&department=${selectedDept}` : "";
                            await downloadFile(`/api/reports/ledger-export-csv?from=${from}&to=${to}${deptParam}`, `general-ledger-${from}_to_${to}.csv`);
                            setShowLedgerDateDialog(false);
                        }} />
                    </div>
                </Dialog>

                <Dialog header="Select Trial Balance Date" visible={showTrialBalanceDialog} style={{ width: '30vw' }} onHide={() => setShowTrialBalanceDialog(false)}>
                    <div className='flex justify-content-end gap-2 mt-3'>
                        <Calendar value={trialBalanceDate} onChange={(e) => setTrialBalanceDate(e.value)} showIcon />
                        <Dropdown value={selectedDept} options={[{ id: null, department: "All Departments" }, ...departments]} optionLabel="department" optionValue="id" onChange={(e) => setSelectedDept(e.value)} placeholder="Filter by Department" className="w-20rem" />
                    </div>
                    <div className="flex justify-content-end gap-2 mt-3">
                        <Button label="Cancel" className="p-button-text" onClick={() => setShowTrialBalanceDialog(false)} />
                        <Button label="Download" onClick={async () => {
                            const asAt = toYmd(trialBalanceDate);
                            const deptParam = selectedDept ? `&department=${selectedDept}` : "";
                            await downloadFile(`/api/reports/trial-balance-csv?as_at=${asAt}${deptParam}`, `trial-balance-${asAt}.csv`);
                            setShowTrialBalanceDialog(false);
                        }} />
                    </div>
                </Dialog>

                <main className="content">
                    <Outlet />
                </main>

                <OperatorSecurityDialog
                    visible={showOperatorSecurity}
                    onHide={() => setShowOperatorSecurity(false)}
                    orgSettings={orgData?.[0]?.custom_json_settings}
                />

                <footer className="footer">
                    <p>&copy; 2024–{new Date().getFullYear()} Goldfish. All rights reserved.</p>
                </footer>
            </div>
        </UserContext.Provider>
    );
};

export default Layout;